import { useState, useEffect, useContext } from 'react';
import { LanguageContext } from './LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faLine, faInstagram, faTwitter, faTiktok, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { setAnalyticsCookies, COOKIE_TYPES } from '../utils/cookie';

function Footer() {
    const { textContent, language } = useContext(LanguageContext);
    const [showCookiePopup, setShowCookiePopup] = useState(false);
    const [showCookieSettings, setShowCookieSettings] = useState(false);
    const [cookiePreferences, setCookiePreferencesState] = useState({
        necessary: true, // Always enabled
        functional: true,
        analytics: true,
    });

    // Helper to get cookie value
    const getCookie = (name) => {
        try {
            const match = document.cookie.match(new RegExp(`(^|;)\\s*${name}=([^;]+)`));
            return match ? decodeURIComponent(match[2]) : null;
        } catch (e) {
            return null;
        }
    };

    // Helper to set a cookie
    const setCookie = (name, value, days) => {
        try {
            let expires = '';
            if (days) {
                const date = new Date();
                date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
                expires = '; expires=' + date.toUTCString();
            }
            document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/';
        } catch (e) {
            // ignore cookie write errors
        }
    };

    // Delete a cookie
    const deleteCookie = (name) => {
        try {
            document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        } catch (e) {
            // ignore
        }
    };

    // Initialize cookie consent state
    useEffect(() => {
        const cookiePrefs = getCookie('cookiePreferences');
        if (!cookiePrefs) {
            setShowCookiePopup(true);
        } else {
            try {
                const prefs = JSON.parse(cookiePrefs);
                setCookiePreferencesState(prefs);
            } catch (e) {
                setShowCookiePopup(true);
            }
        }
    }, []);

    const handleCookieAccept = () => {
        // Accept all cookies except analytics
        const prefs = {
            necessary: true,
            functional: true,
            analytics: false,
        };
        setCookiePreferencesState(prefs);
        setCookie('cookiePreferences', JSON.stringify(prefs), 365);
        setCookie('cookieConsent', 'true', 365);
        setShowCookiePopup(false);
    };

    // const handleCookieReject = () => {
    //     // Only necessary cookies
    //     const prefs = {
    //         necessary: true,
    //         functional: false,
    //         analytics: false,
    //     };
    //     setCookiePreferencesState(prefs);
    //     setCookie('cookiePreferences', JSON.stringify(prefs), 365);
    //     setCookie('cookieConsent', 'false', 365);
    //     // Delete optional cookies
    //     deleteCookie('language');
    //     deleteCookie('_ga');
    //     deleteCookie(COOKIE_TYPES.GA_SESSION);
    //     setShowCookiePopup(false);
    // };

    const handleOpenSettings = () => {
        setShowCookieSettings(true);
    };

    const handleCloseSettings = () => {
        setShowCookieSettings(false);
    };

    const handleSettingChange = (type) => {
        if (type === 'necessary') return; // Cannot change necessary cookies
        setCookiePreferencesState(prev => ({
            ...prev,
            [type]: !prev[type],
        }));
    };

    const handleConfirmSettings = () => {
        // Save preferences
        setCookie('cookiePreferences', JSON.stringify(cookiePreferences), 365);
        setCookie('cookieConsent', 'true', 365);

        // Clean up cookies based on preferences
        if (!cookiePreferences.functional) {
            deleteCookie('language');
        }
        if (!cookiePreferences.analytics) {
            deleteCookie('_ga');
            deleteCookie(COOKIE_TYPES.GA_SESSION);
        } else {
            // If analytics is enabled, set GA cookies immediately
            setAnalyticsCookies();
        }

        setShowCookieSettings(false);
        setShowCookiePopup(false);
    };

    const handleClosePopupButton = () => {
        setShowCookiePopup(false);
    };

    return (
        <>
            <footer className="bg-blue-900 text-white py-8">
                <div className="container mx-auto text-center max-w-none w-full">
                    <div className="text-center mb-4">
                        <h4 className="text-xl font-bold mb-4">{textContent.followUs}</h4>
                        <div className="flex justify-center space-x-4">
                            <a href="https://www.facebook.com/aibiztechnology" target="_blank" rel="noopener noreferrer" className="text-white hover:text-blue-700">
                                <FontAwesomeIcon icon={faFacebook} size="2x" />
                            </a>
                            <a href="https://page.line.me/707ffbfk" target="_blank" rel="noopener noreferrer" className="text-white hover:text-green-700">
                                <FontAwesomeIcon icon={faLine} size="2x" />
                            </a>
                            <a href="https://www.instagram.com/aibiztechofficial" target="_blank" rel="noopener noreferrer" className="text-white hover:text-pink-700">
                                <FontAwesomeIcon icon={faInstagram} size="2x" />
                            </a>
                            <a href="https://x.com/aibiztechnology" target="_blank" rel="noopener noreferrer" className="text-white hover:text-blue-700">
                                <FontAwesomeIcon icon={faTwitter} size="2x" />
                            </a>
                            <a href="https://www.tiktok.com/@aibiztech" target="_blank" rel="noopener noreferrer" className="text-white hover:text-gray-700">
                                <FontAwesomeIcon icon={faTiktok} size="2x" />
                            </a>
                            <a href="https://www.youtube.com/@aibiztech" target="_blank" rel="noopener noreferrer" className="text-white hover:text-red-700">
                                <FontAwesomeIcon icon={faYoutube} size="2x" />
                            </a>
                        </div>
                    </div>
                    <p>{textContent.allRightsReserved}</p>
                    <p className="mt-4">
                        <a href="/privacy-policy" className="text-white hover:underline">
                            {textContent.PrivacyPolicy}
                        </a>
                        {' | '}
                        <a href="/cookie-policy" className="text-white hover:underline">
                            {textContent.CookiePolicy}
                        </a>
                    </p>
                </div>
            </footer>

            {/* Cookie Consent Popup */}
            {showCookiePopup && (
                <div className="fixed bottom-0 left-0 right-0 bg-blue-900 text-white py-4 px-6 shadow-lg z-50">
                    <div className="flex justify-between items-center">
                        <p className="text-sm flex-1">
                            {language === 'th'
                                ? textContent.cookieConsentMsg
                                : textContent.cookieConsentMsg}
                            {' '}
                            <a
                                href="/cookie-policy"
                                className="underline hover:text-gray-300"
                            >
                                {textContent.CookiePolicy}
                            </a>
                        </p>
                        <div className="flex space-x-2 ml-4">
                            
                            <button
                                onClick={handleOpenSettings}
                                className="bg-gray-800 text-white w-[82px] h-[44px] px-4 py-2 rounded-lg hover:bg-gray-700 whitespace-nowrap"
                            >
                                {textContent.settings}
                            </button>
                            <button
                                onClick={handleCookieAccept}
                                className="bg-white text-blue-900 h-[42px] px-4 py-2 rounded-lg hover:bg-gray-200 whitespace-nowrap"
                            >
                                {textContent.accept}
                            </button>
                        </div>
                        <button
                            onClick={handleClosePopupButton}
                            className="ml-4 text-white hover:text-gray-300"
                            aria-label={textContent.close}
                        >
                            <FontAwesomeIcon icon={faTimes} size="lg" />
                        </button>
                    </div>
                </div>
            )}

            {/* Cookie Settings Modal */}
            {showCookieSettings && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ">
                    <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-128 overflow-y-auto">
                        <div className="flex justify-between items-center border-b p-6">
                            <h2 className="text-2xl font-bold text-gray-800">{textContent.cookiePreferences}</h2>
                            <button
                                onClick={handleCloseSettings}
                                className="text-gray-600 hover:text-gray-800"
                                aria-label={textContent.close}
                            >
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-6">
                            {/* Necessary Cookies */}
                            <div className="border rounded-lg p-4 bg-gray-50">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-800">
                                            {textContent.necessaryCookies}
                                        </h3>
                                        <p className="text-gray-600 text-sm mt-1">
                                            {textContent.necessaryCookiesDesc}
                                        </p>
                                    </div>
                                    <div className="ml-4">
                                        <span className="inline-block bg-blue-900 text-white px-3 py-1 rounded-full text-sm font-medium">
                                            {textContent.accept}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Functional Cookies */}
                            <div className="border rounded-lg p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-800">
                                            {textContent.functionalCookies}
                                        </h3>
                                        <p className="text-gray-600 text-sm mt-1">
                                            {textContent.functionalCookiesDesc}
                                        </p>
                                    </div>
                                    <div className="ml-4">
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={cookiePreferences.functional}
                                                onChange={() => handleSettingChange('functional')}
                                                className="w-5 h-5 text-blue-900 rounded focus:ring-2 focus:ring-blue-500"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Analytics Cookies */}
                            <div className="border rounded-lg p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-800">
                                            {textContent.analyticsCookies}
                                        </h3>
                                        <p className="text-gray-600 text-sm mt-1">
                                            {textContent.analyticsCookiesDesc}
                                        </p>
                                    </div>
                                    <div className="ml-4">
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={cookiePreferences.analytics}
                                                onChange={() => handleSettingChange('analytics')}
                                                className="w-5 h-5 text-blue-900 rounded focus:ring-2 focus:ring-blue-500"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t p-6 flex justify-end space-x-2">
                          
                            <button
                                onClick={handleConfirmSettings}
                                className="px-6 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800"
                            >
                                {textContent.confirmSettings}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Footer;